"use client";

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  IconLoader2,
  IconPlus,
  IconTrash,
  IconUserPlus,
} from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInviteAgencyUsers } from "@/hooks/use-agencies";
import { AgencyUserRole } from "@/types";

// Schema idioms deliberately match the zod major that is actually installed
// (3.25) rather than the one package.json pins. `errorMap` is dropped because it
// was removed in zod 4 — the role select is always populated, so the enum never
// needs custom copy anyway.
const inviteAgencyUsersSchema = z.object({
  users: z
    .array(
      z.object({
        email: z.string().email("Please enter a valid email address"),
        firstName: z.string().min(1, "First name is required"),
        lastName: z.string().optional(),
        agencyUserRole: z.nativeEnum(AgencyUserRole),
      })
    )
    .min(1, "At least one user is required"),
});

type InviteAgencyUsersFormData = z.infer<typeof inviteAgencyUsersSchema>;

const emptyRow = {
  email: "",
  firstName: "",
  lastName: "",
  agencyUserRole: AgencyUserRole.MEMBER,
};

interface InviteAgencyUsersDialogProps {
  agencyId: string;
  agencyName: string;
  onSuccess?: () => void;
  children?: React.ReactNode;
}

export function InviteAgencyUsersDialog({
  agencyId,
  agencyName,
  onSuccess,
  children,
}: InviteAgencyUsersDialogProps) {
  const [open, setOpen] = useState(false);

  const mutation = useInviteAgencyUsers(agencyId);
  const isSubmitting = mutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<InviteAgencyUsersFormData>({
    resolver: zodResolver(inviteAgencyUsersSchema),
    defaultValues: { users: [{ ...emptyRow }] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "users",
  });

  const onSubmit = (data: InviteAgencyUsersFormData) => {
    mutation.mutate(
      {
        users: data.users.map((user) => ({
          email: user.email.toLowerCase(),
          firstName: user.firstName,
          lastName: user.lastName || undefined,
          agencyUserRole: user.agencyUserRole,
        })),
      },
      {
        onSuccess: (response) => {
          // A per-row failure comes back inside `results` on a 2xx, so success
          // here does not mean every invitation was sent.
          const { successCount, failureCount, results } = response;

          if (failureCount === 0) {
            toast.success(
              response.message ||
                `Successfully sent ${successCount} invitation(s)`
            );
          } else {
            toast.warning(
              `Sent ${successCount} invitation(s), ${failureCount} failed`
            );

            // Name each address that failed with its reason, so the operator
            // fixes those rather than retyping the batch.
            results.forEach((result) => {
              if (!result.success && result.error) {
                toast.error(`${result.email}: ${result.error}`);
              }
            });
          }

          setOpen(false);
          reset();
          onSuccess?.();
        },
        onError: (error: unknown) => {
          console.error("Error inviting agency users:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Failed to send invitation";
          toast.error(errorMessage);
        },
      }
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Cannot be dismissed mid-submit; resets whenever it closes.
    if (isSubmitting) return;
    setOpen(newOpen);
    if (!newOpen) {
      reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <IconUserPlus className="mr-2 size-4" />
            Invite People
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
          <DialogHeader>
            <DialogTitle>Invite People to {agencyName}</DialogTitle>
            <DialogDescription>
              Send invitations to people to join this agency. They will receive
              an email with instructions to accept.
            </DialogDescription>
          </DialogHeader>

          {/* Column Headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 px-4 pt-4">
            <div>
              <Label>
                Email <span className="text-destructive">*</span>
              </Label>
            </div>
            <div>
              <Label>
                First Name <span className="text-destructive">*</span>
              </Label>
            </div>
            <div>
              <Label>Last Name</Label>
            </div>
            <div>
              <Label>
                Role <span className="text-destructive">*</span>
              </Label>
            </div>
            <div className="w-10"></div>
          </div>

          {/* Scrollable User Rows */}
          <div className="flex-1 overflow-y-auto py-4 px-4 space-y-3 max-h-[50vh]">
            {fields.map((field, index) => (
              <div key={field.id} className="rounded-lg border p-4">
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-start">
                  <div className="grid gap-2">
                    <Input
                      id={`users.${index}.email`}
                      type="email"
                      aria-label={`Email for person ${index + 1}`}
                      placeholder="user@example.com"
                      {...register(`users.${index}.email`)}
                      disabled={isSubmitting}
                    />
                    {errors.users?.[index]?.email && (
                      <p className="text-sm text-destructive">
                        {errors.users[index]?.email?.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Input
                      id={`users.${index}.firstName`}
                      type="text"
                      aria-label={`First name for person ${index + 1}`}
                      placeholder="John"
                      {...register(`users.${index}.firstName`)}
                      disabled={isSubmitting}
                    />
                    {errors.users?.[index]?.firstName && (
                      <p className="text-sm text-destructive">
                        {errors.users[index]?.firstName?.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Input
                      id={`users.${index}.lastName`}
                      type="text"
                      aria-label={`Last name for person ${index + 1}`}
                      placeholder="Doe"
                      {...register(`users.${index}.lastName`)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="grid gap-2">
                    {/* Controlled through the form library rather than faking a
                        change event through the register callback. */}
                    <Controller
                      control={control}
                      name={`users.${index}.agencyUserRole`}
                      render={({ field: roleField }) => (
                        <Select
                          value={roleField.value}
                          onValueChange={roleField.onChange}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger
                            aria-label={`Role for person ${index + 1}`}
                          >
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={AgencyUserRole.ADMIN}>
                              Agency Admin
                            </SelectItem>
                            <SelectItem value={AgencyUserRole.MEMBER}>
                              Agency Member
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.users?.[index]?.agencyUserRole && (
                      <p className="text-sm text-destructive">
                        {errors.users[index]?.agencyUserRole?.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-start pt-0">
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10"
                        aria-label={`Remove person ${index + 1}`}
                        onClick={() => remove(index)}
                        disabled={isSubmitting}
                      >
                        <IconTrash className="h-4 w-4" aria-hidden />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={() => append({ ...emptyRow })}
              disabled={isSubmitting}
              className="w-full"
            >
              <IconPlus className="mr-2 size-4" />
              Add Another Person
            </Button>
          </div>

          {/* Fixed Footer */}
          <DialogFooter className="pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <IconLoader2 className="mr-2 size-4 animate-spin" />
              )}
              Send Invitation{fields.length > 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
